import type { Feature, GeoJSON, Geometry, Position } from "geojson";

import type { GeoItemCreateDraftData, GeoItemCreateFormData } from "~/features/geoItems/types";

const DRAFT_VERSION = 4;
const DRAFT_SCREEN = "standalone-geo-item-create";
const STORAGE_KEY_PREFIX = "dewey:drafts:standalone-geo-item-create";

export interface GeoItemCreateDraftScope {
  userId: string;
}

export interface GeoItemCreateDraft {
  id: string;
  updatedAt: string;
  data: GeoItemCreateDraftData;
}

interface StoredGeoItemCreateDraftData {
  form: Omit<GeoItemCreateFormData, "geometry">;
  areaInputMode: GeoItemCreateDraftData["areaInputMode"];
  resolution: GeoItemCreateDraftData["resolution"];
  geoJsonText: string;
  h3IndexesDraft: string[];
}

interface StoredGeoItemCreateDraft {
  id: string;
  updatedAt: string;
  data: StoredGeoItemCreateDraftData;
}

interface StoredGeoItemCreateDraftEntry extends GeoItemCreateDraftScope {
  version: typeof DRAFT_VERSION;
  screen: typeof DRAFT_SCREEN;
  draft: StoredGeoItemCreateDraft;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const isPosition = (value: unknown): value is Position =>
  Array.isArray(value) && value.length >= 2 && value.every(isFiniteNumber);

const isLineStringCoordinates = (value: unknown): value is Position[] =>
  Array.isArray(value) && value.length >= 2 && value.every(isPosition);

const arePositionsEqual = (left: Position, right: Position): boolean =>
  left.length === right.length && left.every((coordinate, index) => coordinate === right[index]);

const isLinearRing = (value: unknown): value is Position[] =>
  Array.isArray(value) &&
  value.length >= 4 &&
  value.every(isPosition) &&
  arePositionsEqual(value[0], value[value.length - 1]);

const isPolygonCoordinates = (value: unknown): value is Position[][] =>
  Array.isArray(value) && value.length > 0 && value.every(isLinearRing);

const hasValidBbox = (value: Record<string, unknown>): boolean =>
  value.bbox === undefined || (Array.isArray(value.bbox) && value.bbox.every(isFiniteNumber));

const isGeometry = (value: unknown): value is Geometry => {
  if (!isRecord(value) || typeof value.type !== "string" || !hasValidBbox(value)) return false;

  switch (value.type) {
    case "Point":
      return isPosition(value.coordinates);
    case "MultiPoint":
      return Array.isArray(value.coordinates) && value.coordinates.every(isPosition);
    case "LineString":
      return isLineStringCoordinates(value.coordinates);
    case "MultiLineString":
      return Array.isArray(value.coordinates) && value.coordinates.every(isLineStringCoordinates);
    case "Polygon":
      return isPolygonCoordinates(value.coordinates);
    case "MultiPolygon":
      return Array.isArray(value.coordinates) && value.coordinates.every(isPolygonCoordinates);
    case "GeometryCollection":
      return Array.isArray(value.geometries) && value.geometries.every(isGeometry);
    default:
      return false;
  }
};

const isFeature = (value: unknown): value is Feature =>
  isRecord(value) &&
  value.type === "Feature" &&
  hasValidBbox(value) &&
  (value.id === undefined || typeof value.id === "string" || typeof value.id === "number") &&
  (value.properties === null || isRecord(value.properties)) &&
  (value.geometry === null || isGeometry(value.geometry));

const isGeoJson = (value: unknown): value is GeoJSON => {
  if (isGeometry(value) || isFeature(value)) return true;
  return (
    isRecord(value) &&
    value.type === "FeatureCollection" &&
    hasValidBbox(value) &&
    Array.isArray(value.features) &&
    value.features.every(isFeature)
  );
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const isStoredFormData = (value: unknown): value is StoredGeoItemCreateDraftData["form"] =>
  isRecord(value) &&
  typeof value.startDate === "string" &&
  typeof value.endDate === "string" &&
  typeof value.jaLabel === "string" &&
  typeof value.enLabel === "string" &&
  isStringArray(value.h3Indexes) &&
  typeof value.memo === "string";

const isStoredDraftData = (value: unknown): value is StoredGeoItemCreateDraftData =>
  isRecord(value) &&
  isStoredFormData(value.form) &&
  (value.areaInputMode === "h3" || value.areaInputMode === "geoJson") &&
  typeof value.resolution === "number" &&
  Number.isInteger(value.resolution) &&
  value.resolution >= 0 &&
  value.resolution <= 4 &&
  typeof value.geoJsonText === "string" &&
  isStringArray(value.h3IndexesDraft);

const isStoredDraft = (value: unknown): value is StoredGeoItemCreateDraft =>
  isRecord(value) &&
  typeof value.id === "string" &&
  value.id.length > 0 &&
  typeof value.updatedAt === "string" &&
  Number.isFinite(Date.parse(value.updatedAt)) &&
  isStoredDraftData(value.data);

const isStoredDraftEntry = (value: unknown, scope: GeoItemCreateDraftScope): value is StoredGeoItemCreateDraftEntry =>
  isRecord(value) &&
  value.version === DRAFT_VERSION &&
  value.screen === DRAFT_SCREEN &&
  value.userId === scope.userId &&
  isStoredDraft(value.draft);

export const getGeoItemCreateDraftStorageKey = ({ userId }: GeoItemCreateDraftScope): string =>
  `${STORAGE_KEY_PREFIX}:${encodeURIComponent(userId)}`;

const getLocalStorage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const removeStorageItem = (storage: Storage, key: string): void => {
  try {
    storage.removeItem(key);
  } catch {
    // localStorageを利用できなくてもGeoItem作成自体は続けられるようにする。
  }
};

const toStoredDraft = ({ id, updatedAt, data }: GeoItemCreateDraft): StoredGeoItemCreateDraft => {
  const { geometry: _geometry, ...form } = data.form;
  return {
    id,
    updatedAt,
    data: {
      form,
      areaInputMode: data.areaInputMode,
      resolution: data.resolution,
      geoJsonText: data.geoJsonText,
      h3IndexesDraft: data.h3IndexesDraft,
    },
  };
};

export const parseGeoItemCreateDraftGeometry = (geoJsonText: string): GeoJSON | null => {
  if (!geoJsonText.trim()) return null;
  try {
    const candidate: unknown = JSON.parse(geoJsonText);
    return isGeoJson(candidate) ? candidate : null;
  } catch {
    return null;
  }
};

const restoreDraft = ({ id, updatedAt, data }: StoredGeoItemCreateDraft): GeoItemCreateDraft => {
  const geometryDraft = parseGeoItemCreateDraftGeometry(data.geoJsonText);
  return {
    id,
    updatedAt,
    data: {
      ...data,
      form: {
        ...data.form,
        geometry: data.areaInputMode === "geoJson" ? geometryDraft : null,
      },
      geometryDraft,
    },
  };
};

const getDraftStorageKey = (scope: GeoItemCreateDraftScope, draftId: string): string =>
  `${getGeoItemCreateDraftStorageKey(scope)}:${encodeURIComponent(draftId)}`;

const writeDraft = (storage: Storage, scope: GeoItemCreateDraftScope, draft: GeoItemCreateDraft): boolean => {
  try {
    const stored: StoredGeoItemCreateDraftEntry = {
      version: DRAFT_VERSION,
      screen: DRAFT_SCREEN,
      ...scope,
      draft: toStoredDraft(draft),
    };
    storage.setItem(getDraftStorageKey(scope, draft.id), JSON.stringify(stored));
    return true;
  } catch {
    return false;
  }
};

export const loadGeoItemCreateDrafts = (scope: GeoItemCreateDraftScope): GeoItemCreateDraft[] => {
  const storage = getLocalStorage();
  if (!storage) return [];

  const scopeKey = getGeoItemCreateDraftStorageKey(scope);
  const draftKeyPrefix = `${scopeKey}:`;
  let keys: string[];
  try {
    keys = Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter(
      (key): key is string => key?.startsWith(draftKeyPrefix) ?? false,
    );
  } catch {
    return [];
  }
  const drafts: GeoItemCreateDraft[] = [];

  for (const key of keys) {
    try {
      const raw = storage.getItem(key);
      if (!raw) continue;
      const parsed: unknown = JSON.parse(raw);
      if (!isStoredDraftEntry(parsed, scope) || key !== getDraftStorageKey(scope, parsed.draft.id)) {
        removeStorageItem(storage, key);
        continue;
      }
      drafts.push(restoreDraft(parsed.draft));
    } catch {
      removeStorageItem(storage, key);
    }
  }

  removeStorageItem(storage, scopeKey);
  return drafts.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
};

const createDraftId = (existingIds: Set<string>): string => {
  let id: string;
  do {
    id = globalThis.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  } while (existingIds.has(id));
  return id;
};

export const saveGeoItemCreateDraft = (
  scope: GeoItemCreateDraftScope,
  data: GeoItemCreateDraftData,
  draftId?: string,
): GeoItemCreateDraft | null => {
  const storage = getLocalStorage();
  if (!storage) return null;

  const existingIds = new Set(loadGeoItemCreateDrafts(scope).map(({ id }) => id));
  const id = draftId && draftId.length > 0 ? draftId : createDraftId(existingIds);
  const savedDraft: GeoItemCreateDraft = { id, updatedAt: new Date().toISOString(), data };
  return writeDraft(storage, scope, savedDraft) ? savedDraft : null;
};

export const deleteGeoItemCreateDraft = (scope: GeoItemCreateDraftScope, draftId: string): void => {
  const storage = getLocalStorage();
  if (!storage) return;

  removeStorageItem(storage, getDraftStorageKey(scope, draftId));
};
